import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import {
  Paper,
  Stack,
  Title,
  Divider,
  ScrollArea,
  Collapse,
  Tabs,
  useMantineTheme,
  useComputedColorScheme,
  UnstyledButton,
  Box,
  Badge,
} from "@mantine/core";
import classes from "./SettingsNav.module.css";
import {
  IconChevronRight,
  IconLifebuoy,
  IconLogout,
} from "@tabler/icons-react";
import { getAuth } from "firebase/auth";

import { runBeforeRouteChange } from "@/app/_utils/navigationHookRegistry";
import { PAGE_SHELL_PADDING } from "@/app/_constants/ui";
import ThemeToggle2 from "@/app/_components/ThemeToggle/ThemeToggle2";
import { useSupportContext } from "@/app/_providers/SupportProvider/SupportProvider";
import useSettingsTabs from "./useSettingsTabs";

const SettingsNav = () => {
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const auth = getAuth();
  const { open: openSupport, unreadCount: supportUnreadCount } =
    useSupportContext();

  // Shared with the mobile drawer so the two settings navs can't drift.
  const settingsTabs = useSettingsTabs();

  // Get current tab and subtab from URL
  const currentTab = searchParams.get("tab");
  const currentSubtab = searchParams.get("subtab");

  const [openSubTabs, setOpenSubTabs] = useState<Set<string>>(() => {
    // If payments or contracts tab is active, expand it by default
    if (currentTab === "payments") {
      return new Set(["payments"]);
    }
    if (currentTab === "contracts") {
      return new Set(["contracts"]);
    }
    return new Set();
  });

  return (
    <Paper
      h="100dvh"
      w={225}
      p="md"
      // Match PageShell's top padding so the "Settings" heading sits on
      // the same line as each tab's SettingsSectionTitle
      pt={PAGE_SHELL_PADDING}
      pr={0}
      pl={0}
      radius={0}
      style={{
        borderRight: `1px solid ${
          computedColorScheme === "light"
            ? theme.colors.gray[2]
            : theme.colors.dark[5]
        }`,
      }}
    >
      <Stack gap={0} h="100%">
        <Stack pl={20} gap={0}>
          {/* Line box pinned to 35px so the divider lands at the same y
              as every settings tab's SettingsSectionTitle divider */}
          <Title order={3} style={{ lineHeight: "35px" }}>
            Settings
          </Title>

          <Divider mt="sm" mb="sm" mr="md" />
        </Stack>

        <ScrollArea
          h="calc(100dvh - (16px + 30px + 24px))"
          scrollbars="y"
          offsetScrollbars={"x"}
        >
          <Stack gap={0} pr="md">
            {settingsTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive =
                pathname.includes(`/settings`) &&
                (currentTab === tab.key ||
                  (tab.key === "general" && !currentTab));

              return (
                <Stack key={tab.key} w="100%" gap="xs">
                  <UnstyledButton
                    className={classes.link}
                    data-active={isActive || undefined}
                    onClick={() => {
                      if (tab.subTabs) {
                        if (isActive) {
                          return;
                        }
                        setOpenSubTabs((prev) => {
                          const newSet = new Set<string>();
                          if (!prev.has(tab.key)) {
                            newSet.add(tab.key);
                          }
                          return newSet;
                        });
                        runBeforeRouteChange(() => router.push(tab.path));
                      } else {
                        runBeforeRouteChange(() => router.push(tab.path));
                        setOpenSubTabs(new Set());
                      }
                    }}
                    style={{ cursor: isActive ? "default" : "pointer" }}
                  >
                    <Box className={classes.linkInner}>
                      <Icon size={16} />
                      {tab.label}
                    </Box>
                    {tab.subTabs && (
                      <IconChevronRight
                        size={16}
                        stroke={1.5}
                        className={classes.chevron}
                        data-opened={openSubTabs.has(tab.key) || undefined}
                      />
                    )}
                  </UnstyledButton>

                  {tab.subTabs && (
                    <Collapse in={openSubTabs.has(tab.key)}>
                      <Tabs
                        value={
                          currentSubtab ||
                          (currentTab === "payments" ? "transactions" : null) ||
                          (currentTab === "contracts" ? "templates" : null)
                        }
                        orientation="vertical"
                        placement="right"
                        display="flex"
                        pl="xl"
                        styles={{
                          root: {
                            justifyContent: "start",
                          },
                          list: {
                            width: "100%",
                          },
                        }}
                        w="100%"
                      >
                        <Tabs.List>
                          {tab.subTabs.map((subTab) => {
                            const SubIcon = subTab.icon;
                            return (
                              <Tabs.Tab
                                key={subTab.key}
                                value={subTab.key}
                                onClick={() => {
                                  runBeforeRouteChange(() =>
                                    router.push(subTab.path)
                                  );
                                }}
                                leftSection={<SubIcon size={14} />}
                                // size="xs"
                                // style={{
                                //   fontWeight: isSubActive ? 500 : 400,
                                // }}
                              >
                                {subTab.label}
                              </Tabs.Tab>
                            );
                          })}
                        </Tabs.List>
                      </Tabs>

                      {/* <Stack gap="xs">
                        {tab.subTabs.map((subTab) => {
                          const SubIcon = subTab.icon;
                          const isSubActive =
                            currentSubtab === subTab.key ||
                            (!currentSubtab && subTab.key === "payments");

                          return (
                            <Button
                              key={subTab.key}
                              variant={isSubActive ? "light" : "subtle"}
                              color={isSubActive ? "blue" : "gray"}
                              justify="start"
                              leftSection={<SubIcon size={14} />}
                              size="xs"
                              ml="md"
                              mt="xs"
                              onClick={() => {
                                runBeforeRouteChange(() =>
                                  router.push(subTab.path)
                                );
                              }}
                              style={{
                                fontWeight: isSubActive ? 500 : 400,
                              }}
                            >
                              {subTab.label}
                            </Button>
                          );
                        })}
                      </Stack> */}
                    </Collapse>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </ScrollArea>

        <Stack gap={0} pr="md" mt="auto" pt="md">
          <Divider mb="sm" ml="md" />
          <UnstyledButton
            className={`${classes.link} Report-WalkThrough`}
            onClick={() => openSupport()}
          >
            <Box className={classes.linkInner}>
              <IconLifebuoy size={16} />
              Support
              {supportUnreadCount > 0 && (
                <Badge size="xs" circle color="red" ml={4}>
                  {supportUnreadCount}
                </Badge>
              )}
            </Box>
          </UnstyledButton>
          <UnstyledButton
            className={`${classes.link} Logout-WalkThrough`}
            onClick={() => auth.signOut()}
          >
            <Box className={classes.linkInner}>
              <IconLogout size={16} />
              Logout
            </Box>
          </UnstyledButton>
          <Box className="Dark-WalkThrough" pl="md" py={4}>
            <ThemeToggle2 />
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default SettingsNav;
